/* OOTB Code
We have overridden this file to trigger an event after show more ajax success to re-init lazy load and animate up */

'use strict';

const baseSearch = require('base/search/search');
const { queryFirst, updateUrlQueryParam, addClass, removeClass, queryAll, scrollTo, hasClass, renderFragment, getCookieMap, throttle, debounce, matchesBreakpoint, getNavigationType } = require('../domUtil');
const { appendToUrl, formatMessage, getJSON } = require('../util');
const { emptySwatchTemplate } = require('../templates');
const { imageAndQuickbuyHoverHandler } = require('../product/helper');
const { HIDDEN_CLASS, INVISIBLE_CLASS } = require('../constants');
const $body = $('body');
const $container = $('.container');
const $searchResultsContainer = $('.search-results.container');
const $refinementBar = $('.refinement-bar');
const $modalBG = $('.modal-background');
const REFINEMENTS_CONTAINER_SELECTOR = '.refinements-container';
const PLP_CONFIG_PARAMS = ['pmin', 'pmax', 'srule', 'storeID', 'q', 'cgid', 'featured'];
const PLP_REFINEMENT_PARAM = /^pref[nv]\d+$/;
const PLP_PRODUCT_CELL_SELECTOR = '.product-grid .product-tile-container:not(.featured-overflow)';
const { lp_hsc_enabled: akamaiEnabled, lp_hsc_category: plpCaching, lp_cache_hash: lpCacheHash, lp_group_hash: lpGroupHash } = getCookieMap();
const isAkamaiEnabled = (akamaiEnabled === 'true' && plpCaching && lpCacheHash && lpGroupHash);
const akamaiSettings = (queryFirst('#akamai-settings') || { dataset: {} }).dataset;
let cacheSyncTimer;
const plpHeader = queryFirst('.clp-results-header');
const mainHeader = queryFirst('.main-header');
let searchIntersectionObserver = null;
let plpRestoreFinished = false;
let plpBackfillDone = false;
let plpHistoryRestorePending = false;
// Read once at load: ShowAjax replaces .product-grid with a standard one that has no PD marker.
const isPdGridDocument = !!queryFirst('.product-grid[data-pd-grid="true"]');
let renderedPlpUrl = window.location.href;
let plpResultsRequestId = 0;
const FILTER_PANEL_ANIMATION_MS = 500;

/**
 * Initializes infinite scroll when the PLP is configured for it.
 */
function initInfiniteScrollIfEnabled() {
    if ($('.infinite-scroll-enabled').length > 0) {
        initialiseSearchIntersection();
    }
}

/**
 * Disconnects the infinite-scroll intersection observer.
 */
function teardownInfiniteScrollObserver() {
    if (searchIntersectionObserver) {
        searchIntersectionObserver.disconnect();
        searchIntersectionObserver = null;
    }
}

/**
 * @returns {number} scroll offset that puts the results header right below the sticky main header
 */
function getResultsTop() {
    return plpHeader && mainHeader ? plpHeader.offsetTop - mainHeader.offsetHeight : 0;
}

/**
 * Reads the site default page size used for page=N bucket lookup.
 * @param {Element} productGrid - product grid element
 * @param {Element} [gridFooter] - grid footer element
 * @returns {number}
 */
function getDefaultPageSize(productGrid, gridFooter) {
    return parseInt(productGrid && productGrid.dataset.defaultPageSize, 10)
        || parseInt(gridFooter && gridFooter.dataset.defaultPageSize, 10)
        || 48;
}

/**
 * Converts a zero-based start offset to a 1-based page number.
 * @param {number} start - zero-based offset
 * @param {number} defaultPageSize - bucket size
 * @returns {number}
 */
function getPageNumFromStart(start, defaultPageSize) {
    return Math.floor(start / defaultPageSize) + 1;
}

/**
 * Resolves the start offset from URL params only (`page`, then `start`).
 * @param {URLSearchParams} urlParams - URL search params
 * @param {number} defaultPageSize - bucket size
 * @returns {number} zero-based product offset
 */
function getStartFromUrlParams(urlParams, defaultPageSize) {
    const pageParam = parseInt(urlParams.get('page'), 10);

    if (pageParam > 1) {
        return (pageParam - 1) * defaultPageSize;
    }

    return parseInt(urlParams.get('start'), 10) || 0;
}

/**
 * Resolves the current start offset from URL params or data attributes.
 * @param {URLSearchParams} urlParams - current URL search params
 * @param {Element} productGrid - product grid element
 * @param {Element} [gridFooter] - grid footer element
 * @returns {number}
 */
function getCurrentStartFromUrl(urlParams, productGrid, gridFooter) {
    const serverStart = parseInt(productGrid && productGrid.dataset.pageStart, 10);
    if (!isNaN(serverStart) && serverStart > 0) {
        return serverStart;
    }

    return getStartFromUrlParams(urlParams, getDefaultPageSize(productGrid, gridFooter));
}

/**
 * @returns {number} zero-based product offset this document was loaded with
 */
function getDocumentPageStart() {
    return getCurrentStartFromUrl(
        new URLSearchParams(window.location.search),
        queryFirst('.product-grid'),
        queryFirst('.grid-footer')
    );
}

/**
 * @param {number} pageStart - zero-based product offset from the URL
 * @returns {Element|null} first tile (or marker) for that page
 */
function getDeepLinkScrollTarget(pageStart) {
    const productGrid = queryFirst('.product-grid');
    if (!productGrid) {
        return null;
    }

    const marker = queryFirst('.page-boundary-marker[data-page-start="' + pageStart + '"]', productGrid);
    if (marker) {
        let sibling = marker.nextElementSibling;
        while (sibling && (
            sibling.classList.contains('page-boundary-marker')
            || sibling.classList.contains('skeleton-tile')
            || sibling.classList.contains('featured-overflow')
        )) {
            sibling = sibling.nextElementSibling;
        }
        if (sibling && sibling.classList.contains('product-tile-container')) {
            return sibling;
        }
        return marker;
    }

    return queryFirst('.product-tile-container:not(.skeleton-tile)', productGrid);
}

/**
 * @returns {Object} state of the current history entry, or an empty object when it has none
 */
function getEntryState() {
    const state = window.history.state;
    return state && typeof state === 'object' ? state : {};
}

/**
 * Merges the given fields into the current history entry state, keeping its URL.
 * @param {Object} patch - fields to set
 */
function patchEntryState(patch) {
    window.history.replaceState(
        Object.assign({}, getEntryState(), patch),
        '',
        window.location.pathname + window.location.search
    );
}

/**
 * @returns {string} persisted tile element id, if any
 */
function getPersistedTileId() {
    return getEntryState().plpTileId || '';
}

/**
 * Claims restoration on the entry we are leaving. Safari only skips its own (wrong)
 * restore when the entry is already manual as the Back navigation starts, which is
 * too late to arrange once that document is loading.
 */
function claimScrollRestoration() {
    if (window.history.scrollRestoration) {
        window.history.scrollRestoration = 'manual';
    }
}

/**
 * @returns {number|null} viewport offset the tile had when it was clicked
 */
function getPersistedTileTop() {
    const tileTop = getEntryState().plpTileTop;
    return typeof tileTop === 'number' ? tileTop : null;
}

/**
 * @param {Element} tile - element with a product-tile-* id
 * @returns {number} position of its grid cell among product cells, skeletons included, or -1
 */
function getTileCellIndex(tile) {
    const cell = tile.closest('.product-tile-container');
    return cell ? queryAll(PLP_PRODUCT_CELL_SELECTOR).indexOf(cell) : -1;
}

/**
 * Stores the clicked PLP tile and its viewport offset on this history entry (no URL hash).
 * @param {Event} event - click event
 */
function persistClickedTile(event) {
    let el = event.target;
    while (el && el !== document) {
        if (el.id && el.id.indexOf('product-tile-') === 0) {
            patchEntryState({
                plpTileId: el.id,
                plpTileTop: Math.round(el.getBoundingClientRect().top),
                plpTileIndex: getTileCellIndex(el)
            });
            claimScrollRestoration();
            return;
        }
        el = el.parentNode;
    }
}

/**
 * @returns {Element} topmost tile still visible below the sticky header, or null
 */
function getTopVisibleTile() {
    const headerHeight = mainHeader ? mainHeader.offsetHeight : 0;
    const tiles = queryAll('.product-grid [id^="product-tile-"]');

    for (let i = 0; i < tiles.length; i++) {
        if (tiles[i].getBoundingClientRect().bottom > headerHeight + 1) {
            return tiles[i];
        }
    }

    return null;
}

/**
 * Keeps the topmost visible tile on this history entry so a reload restores the exact spot.
 */
function persistAnchorTile() {
    if (!plpRestoreFinished || plpHistoryRestorePending) {
        return;
    }

    const tile = getTopVisibleTile();
    if (!tile) {
        return;
    }

    const tileTop = Math.round(tile.getBoundingClientRect().top);
    const tileIndex = getTileCellIndex(tile);
    const prev = getEntryState();
    if (prev.plpTileId === tile.id && prev.plpTileTop === tileTop && prev.plpTileIndex === tileIndex) {
        return;
    }

    patchEntryState({ plpTileId: tile.id, plpTileTop: tileTop, plpTileIndex: tileIndex });
}

/**
 * Records whether the desktop filter panel is open on the current history entry.
 */
function persistFilterPanelState() {
    if (!matchesBreakpoint('md')) {
        return;
    }

    const open = !hasClass(queryFirst('.search-results-container'), 'hidden-filters');
    const state = window.history.state;
    if (state && typeof state === 'object' && !!state.plpFiltersOpen === open) {
        return;
    }

    patchEntryState({ plpFiltersOpen: open });
}

/**
 * Keeps the anchor tile and the filter panel state of this history entry current.
 */
function persistPlpEntryState() {
    persistAnchorTile();
    persistFilterPanelState();
}

/**
 * @param {Element} el - element to align inside the viewport
 * @param {number|null} viewportTop - offset to place the element at, or null for below the header
 */
function scrollWindowToElement(el, viewportTop) {
    if (!el) {
        return;
    }
    const headerHeight = mainHeader ? mainHeader.offsetHeight : 0;
    const desiredTop = viewportTop === null ? headerHeight : viewportTop;
    const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const top = Math.max(
        Math.round(currentY + el.getBoundingClientRect().top - desiredTop),
        0
    );
    window.scrollTo(0, top);
}

/**
 * @returns {Object} tile persisted on the current history entry plus its viewport offset, or null
 */
function getPersistedTileTarget() {
    const tileId = getPersistedTileId();
    const tile = tileId ? document.getElementById(tileId) : null;
    return tile ? { element: tile, viewportTop: getPersistedTileTop() } : null;
}

/**
 * @returns {Object} skeleton standing in the persisted tile's grid cell plus the tile's viewport offset, or null
 */
function getPersistedSkeletonTarget() {
    const index = getEntryState().plpTileIndex;
    if (typeof index !== 'number' || index < 0) {
        return null;
    }
    const cell = queryAll(PLP_PRODUCT_CELL_SELECTOR)[index];
    return cell && hasClass(cell, 'skeleton-tile') ? { element: cell, viewportTop: getPersistedTileTop() } : null;
}

/**
 * @param {number} pageStart - zero-based product offset
 * @returns {Object} first row of that page below the header, or null when the page is not rendered
 */
function getPageBoundaryTarget(pageStart) {
    const marker = getDeepLinkScrollTarget(pageStart);
    return marker ? { element: marker, viewportTop: null } : null;
}

/**
 * @param {number} pageStart - zero-based product offset of the history entry
 * @returns {Object} persisted tile, else the skeleton in its cell, else the first row of its page
 */
function getHistoryEntryTarget(pageStart) {
    return getPersistedTileTarget() || getPersistedSkeletonTarget() || getPageBoundaryTarget(pageStart);
}

/**
 * @returns {boolean} true when the persisted tile sits in a page that backfill has not rendered yet and has no skeleton to stand in
 */
function isPersistedTileAwaitingBackfill() {
    return !!getPersistedTileId()
        && !plpBackfillDone
        && !getPersistedTileTarget()
        && !getPersistedSkeletonTarget()
        && !!queryFirst('.product-grid .skeleton-tile');
}

/**
 * Back/Forward: the persisted tile at its original viewport offset, else the first row of ?page=N.
 * Fresh navigation to ?page=N: first row of that page.
 * Reload with a persisted tile: the persisted tile, else the first row of ?page=N, because the native restore lands only after the first paint.
 * Reload otherwise: nothing, the native restore applies.
 * @param {number} pageStart - zero-based product offset from the URL
 * @returns {Object} anchor element plus its viewport offset, or null when nothing to restore
 */
function getPlpRestoreTarget(pageStart) {
    const navigationType = getNavigationType();

    if (navigationType === 'back_forward') {
        if (!pageStart) {
            return getPersistedTileTarget();
        }
        return isPersistedTileAwaitingBackfill() ? null : getHistoryEntryTarget(pageStart);
    }

    if (navigationType === 'reload') {
        return getPersistedTileId() && !isPersistedTileAwaitingBackfill() ? getHistoryEntryTarget(pageStart) : null;
    }

    if (!pageStart) {
        return null;
    }

    return getPageBoundaryTarget(pageStart);
}

/**
 * @param {number} pageStart - zero-based product offset from the URL
 * @returns {boolean} true when this load needs us to place the viewport
 */
function shouldRestorePlpScroll(pageStart) {
    const navigationType = getNavigationType();

    if (navigationType === 'back_forward') {
        return !!getPersistedTileId() || !!pageStart;
    }
    if (navigationType === 'reload') {
        return !!getPersistedTileId();
    }

    return !!pageStart;
}

/**
 * @param {number} pageStart - zero-based product offset from the URL
 * @returns {boolean} true when the viewport was placed on the restore target
 */
function applyPlpScrollRestore(pageStart) {
    const target = getPlpRestoreTarget(pageStart);
    if (!target) {
        return false;
    }

    scrollWindowToElement(target.element, target.viewportTop);
    return true;
}

/**
 * @returns {boolean} true when this load places the persisted tile and must re-place it after backfill
 */
function isTileRestoreLoad() {
    const navigationType = getNavigationType();
    return navigationType === 'back_forward' || (navigationType === 'reload' && !!getPersistedTileId());
}

/**
 * Takes over scroll restoration only for Back/Forward, where Safari lands on zero,
 * and for a reload with a persisted tile, which is placed while the results are still hidden.
 * The mode sticks to the history entry across reloads, so it has to be reset explicitly.
 */
function syncScrollRestorationMode() {
    if (!window.history.scrollRestoration) {
        return;
    }
    const ownsRestore = isTileRestoreLoad() && shouldRestorePlpScroll(getDocumentPageStart());
    window.history.scrollRestoration = ownsRestore ? 'manual' : 'auto';
}

syncScrollRestorationMode();

/**
 * Shows the page content that util/plpRestoreCurtain hid until the restored position is in place.
 */
function revealPlpContent() {
    const content = document.getElementById('main-content');
    if (content) {
        content.style.visibility = '';
    }
}

/**
 * Places the viewport. Back/Forward and reload with a persisted tile stay open for one more
 * placement until backfill reports done, because inserting previous pages pushes the anchor tile down.
 * @param {number} pageStart - zero-based product offset from the URL
 */
function startPlpScrollRestore(pageStart) {
    if (plpRestoreFinished) {
        return;
    }

    let placed = false;
    if (!shouldRestorePlpScroll(pageStart)) {
        plpRestoreFinished = true;
    } else if (applyPlpScrollRestore(pageStart)) {
        placed = true;
        if (plpBackfillDone || !isTileRestoreLoad()) {
            plpRestoreFinished = true;
        }
    } else if (plpBackfillDone) {
        plpRestoreFinished = true;
    }

    if (placed || plpRestoreFinished) {
        revealPlpContent();
    }
}

/**
 * Marks the backfill as finished and places the viewport against the settled DOM.
 * @param {number} pageStart - zero-based product offset from the URL
 */
function onPlpBackfillDone(pageStart) {
    plpBackfillDone = true;
    startPlpScrollRestore(pageStart);
}

/**
 * @param {string} urlStr - relative or absolute URL
 * @returns {string} pathname plus query, hash ignored
 */
function getUrlKey(urlStr) {
    const url = new URL(urlStr, window.location.origin);
    return url.pathname + url.search;
}

/**
 * Drops page/start/sz from a relative or absolute URL (filter AJAX resets to first page).
 * @param {string} urlStr - URL to clean
 * @returns {string} pathname + search without pagination params
 */
function omitPaginationParams(urlStr) {
    try {
        const url = new URL(urlStr, window.location.origin);
        url.searchParams.delete('page');
        url.searchParams.delete('start');
        url.searchParams.delete('sz');
        return url.pathname + url.search;
    } catch (e) {
        return urlStr;
    }
}

/**
 * Removes pagination params from the current browser URL.
 */
function stripPaginationFromBrowserUrl() {
    const url = new URL(window.location.href);
    let changed = false;

    ['page', 'start', 'sz'].forEach(function (key) {
        if (url.searchParams.has(key)) {
            url.searchParams.delete(key);
            changed = true;
        }
    });

    if (!changed) {
        return;
    }

    const newUrl = url.pathname + url.search + url.hash;
    window.history.replaceState(window.history.state, '', newUrl);
}

/**
 * Pushes one history entry for a sort change, without pagination params and without touching the entry being left.
 * @param {DocumentFragment} fragment - parsed AJAX response
 */
function updateUrlAfterPaginationReset(fragment) {
    const permaLink = queryFirst('.permalink', fragment);
    const queryParamsElement = queryFirst('.append-params', fragment);
    const writeUrl = function (newUrl) {
        const sameEntry = getUrlKey(newUrl) === getUrlKey(window.location.href);
        window.history[sameEntry ? 'replaceState' : 'pushState']({ path: newUrl }, '', newUrl);
    };

    if (permaLink && permaLink.value) {
        const linkUrl = new URL(permaLink.value, window.location.origin);
        linkUrl.searchParams.delete('page');
        linkUrl.searchParams.delete('start');
        linkUrl.searchParams.delete('sz');
        writeUrl(linkUrl.pathname + linkUrl.search + linkUrl.hash);
        return;
    }

    if (queryParamsElement && queryParamsElement.value) {
        const baseUrl = window.location.href.split('?')[0];
        const params = new URLSearchParams(queryParamsElement.value);
        params.delete('page');
        params.delete('start');
        params.delete('sz');
        const qs = params.toString();
        writeUrl(qs ? baseUrl + '?' + qs : baseUrl);
    }
}

/**
 * @param {string} name - query param name
 * @returns {boolean} true when the param defines which results the PLP shows
 */
function isPlpConfigParam(name) {
    return PLP_REFINEMENT_PARAM.test(name) || PLP_CONFIG_PARAMS.indexOf(name) !== -1;
}

/**
 * @param {string} urlStr - relative or absolute URL
 * @returns {string} sorted PLP configuration params, ignoring paging and tracking params
 */
function getPlpConfigKey(urlStr) {
    const pairs = [];
    new URL(urlStr, window.location.origin).searchParams.forEach((value, key) => {
        if (isPlpConfigParam(key)) {
            pairs.push(key + '=' + value);
        }
    });
    return pairs.sort().join('&');
}

/**
 * @param {string} urlStr - relative or absolute URL
 * @returns {string} URL pathname
 */
function getUrlPathname(urlStr) {
    return new URL(urlStr, window.location.origin).pathname;
}

/**
 * Records the current browser URL as the state the PLP DOM shows, and keeps the full result
 * configuration on the history entry: SEO paths (e.g. /clothing/yellow2) carry refinements the URL query does not.
 * @param {ParentNode} [source] - response fragment the grid is rendered from, defaults to the document
 */
function markPlpRendered(source) {
    renderedPlpUrl = window.location.href;
    const updateGridUrlInput = queryFirst('.ajax-update-grid-url', source || document);
    if (updateGridUrlInput && updateGridUrlInput.value && getEntryState().plpConfigUrl !== updateGridUrlInput.value) {
        patchEntryState({ plpConfigUrl: updateGridUrlInput.value });
    }
}

/**
 * Checks the store pickup toggle exactly when the current URL carries a store filter.
 */
function syncStoreToggleWithLocation() {
    const storeToggle = queryFirst('.store-filter-toggle');
    if (storeToggle) {
        storeToggle.checked = new URLSearchParams(window.location.search).has('storeID');
    }
}

/**
 * @returns {URLSearchParams} params of the recorded PLP configuration of the current history entry, else of the location query
 */
function getHistoryEntryConfigParams() {
    const configUrl = getEntryState().plpConfigUrl;
    return configUrl
        ? new URL(configUrl, window.location.origin).searchParams
        : new URLSearchParams(window.location.search);
}

/**
 * @returns {boolean} true when the history entry configuration carries refinements or a price range
 */
function isHistoryEntryRefined() {
    const params = getHistoryEntryConfigParams();
    return params.has('pmin') || params.has('pmax') || Array.from(params.keys()).some(function (key) {
        return PLP_REFINEMENT_PARAM.test(key);
    });
}

/**
 * @param {string} baseUrl - Search-ShowAjax URL with category or keyword context only
 * @returns {string} relative ShowAjax URL for the PLP configuration of the current history entry
 */
function buildShowAjaxUrlForLocation(baseUrl) {
    const url = new URL(baseUrl, window.location.origin);
    getHistoryEntryConfigParams().forEach((value, key) => {
        if (isPlpConfigParam(key)) {
            url.searchParams.set(key, value);
        }
    });
    return url.pathname + url.search;
}

/**
 * Function that updates action url after every ajax call
 * @param {string} actionUrl action url
 */
function updateRefinementsAjaxUrl(actionUrl) {
    const refinementsContainer = queryFirst(REFINEMENTS_CONTAINER_SELECTOR);
    if (!refinementsContainer) return;

    refinementsContainer.dataset.actionUrl = actionUrl;
}

/**
 * Append Store filter params to action Url
 * @param {HTMLElement} storeToggleEl the element which triggered the filter
 * @param {string} actionUrl action url
 * @returns {string} updated action url
 */
function appendStoreFilterQueryParams(storeToggleEl, actionUrl) {
    // If event is triggered on storeToggle Checkbox, then proceed with url updation
    if (hasClass(storeToggleEl, 'store-filter-toggle')) {
        const STORE_ID_PARAM = 'storeID';
        const { storeId } = storeToggleEl.dataset;

        // Updating relative URL query params using URL api
        const url = new URL(location.origin + actionUrl);
        const { searchParams } = url;
        if (storeToggleEl.checked) {
            searchParams.set(STORE_ID_PARAM, storeId);
        } else {
            searchParams.delete(STORE_ID_PARAM);
        }
        url.search = searchParams.toString();

        // url.pathname -> "/on/demandware.store/Sites-lillypulitzer-sfra-Site/default/Search-ShowAjax"
        // url.search -> "?cgid=dresses-1-all&prefn1=refinementCategoryDresses&prefv1=Shift%7cA-Line%2fEasy%7cStraight"
        return url.pathname + url.search;
    }
    return actionUrl;
}

/**
 * Update sort option URLs from Ajax response
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {string} - Selected sort.
 */
function updateSortOptions(fragment) {
    const toggleEl = queryFirst('.sort-order-menu-toggle span');
    const gridFooter = queryFirst('.grid-footer', fragment);

    if (gridFooter && toggleEl) {
        const dataSortLabel = gridFooter.dataset.sortLabel;
        toggleEl.textContent = dataSortLabel;

        const { options: sortOptions, ruleId: selectedRule } = JSON.parse(gridFooter.dataset.sortOptions);

        sortOptions.forEach(function (option) {
            const $sortOption = $('.sort-order-menu-dropdown button[data-id="' + option.id + '"], button[data-id="' + option.id + '"].sort-order-menu-option');
            $sortOption.val(option.url);
            if (option.id === selectedRule) {
                $sortOption.addClass('selected');
                $sortOption.attr('aria-selected', 'true');
                toggleEl.textContent = option.displayName;
            } else {
                $sortOption.removeClass('selected');
                $sortOption.attr('aria-selected', 'false');
            }
        });
    }
    return toggleEl ? toggleEl.textContent : '';
}

/**
 * Update DOM elements with Ajax results
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @param {string} selector - DOM element to look up in the $results
 * @return {undefined}
 */
function updateDom(fragment, selector) {
    const containers = queryAll(selector);
    const updates = queryFirst(selector, fragment);

    if (containers.length && updates) {
        containers.forEach(container => {
            const clone = updates.cloneNode(true);

            container.parentElement.replaceChild(clone, container);
        });
    }
}

/**
 * Keep refinement panes expanded/collapsed after Ajax refresh
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {undefined}
 */
function handleRefinements(fragment) {
    $('.refinement.active').each(function () {
        $(this).removeClass('active');
        var activeDiv = queryFirst('.' + $(this)[0].className.replace(/ /g, '.'), fragment);
        addClass(activeDiv, 'active');
        queryFirst('button.title', activeDiv).setAttribute('aria-expanded', 'true');
    });

    updateDom(fragment, '.refinements');
}

/**
 * Parse Ajax results and updated select DOM elements
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 */
function parseResults(fragment) {
    var specialHandlers = {
        '.refinements': handleRefinements
    };

    // Update DOM elements that do not require special handling
    ['.grid-header', '.header-bar', '.header.page-title', '.product-grid', '.show-more', '.filter-bar'].forEach(function (selector) {
        updateDom(fragment, selector);
    });

    Object.keys(specialHandlers).forEach(function (selector) {
        specialHandlers[selector](fragment);
    });
}

/**
 * Update search results count
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {number} Returns the total number of search results
 */
function updateCount(fragment) {
    const productCount = queryFirst('.current-product-count', fragment);
    const resultCounts = queryAll('.result-count');

    if (!productCount) {
        const noFilterResults = queryFirst('.no-filter-results', fragment);

        if (noFilterResults) {
            resultCounts.forEach(item => item.textContent = noFilterResults.dataset.noproductcount);
        }
    } else {
        resultCounts.forEach(item => item.textContent = productCount.value);
    }

    const container = queryFirst('.no-filter-results');

    if (container) {
        const numOfProducts = parseInt(resultCounts[0].innerText.replace(/\D/g, ''), 10);

        if (numOfProducts > 0) {
            addClass(container, HIDDEN_CLASS);
        } else {
            removeClass(container, HIDDEN_CLASS);
        }
    }

    return parseInt((resultCounts[0].innerText.split(' result ')[0] || '0').replace(/\D/g, ''), 10);
}

/**
 * Count the number of filters applied
 * @param {Element} element - The element containing the filter count
 * @returns {string} A display string of the filter count
 */
function refinementFilterCount(element) {
    if (!element) return '';

    let filterText = element.innerText;
    let filterAppliedCount = element.dataset.filterCount;
    const storeFilterToggle = queryFirst('.store-filter-toggle');
    const isStoreFilterApplied = storeFilterToggle && storeFilterToggle.checked;

    if (isStoreFilterApplied) {
        filterAppliedCount = parseInt(filterAppliedCount, 10) + 1;
        filterText = `${element.dataset.filterLabel} (${filterAppliedCount})`;
    }

    return filterText;
}

/**
 * Update filter count
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {undefined}
 */
function updateFilterCount(fragment) {
    const filterCount = queryFirst('.selected-filter-count', fragment);
    const productCount = queryFirst('.no-filter-results', fragment);
    const selectedFilterCount = queryFirst('.selected-filter-count');
    const showFilterSortHeader = queryFirst('.show-filter-sort-header');
    const correctFilterCount = filterCount || selectedFilterCount;
    const displayClass = 'd-md-flex';

    if (selectedFilterCount) {
        selectedFilterCount.textContent = refinementFilterCount(correctFilterCount);
    }

    // This will show/hide desktop filter and sorting fillter in desktop if product count is zero
    if (!productCount || productCount.dataset.productcount !== '0') {
        addClass(showFilterSortHeader, displayClass);
    } else {
        removeClass(showFilterSortHeader, displayClass);
    }
}

/**
 * Update show no results message
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {undefined}
 */
function showNoResultsText(fragment) {
    const noFilterResults = queryFirst('.no-filter-results', fragment);
    if (noFilterResults) {
        const numOfProducts = parseInt(noFilterResults.dataset.productcount, 10);
        if (numOfProducts > 0 && !noFilterResults.classList.contains(HIDDEN_CLASS)) {
            noFilterResults.classList.add(HIDDEN_CLASS);
        } else {
            noFilterResults.classList.remove(HIDDEN_CLASS);
        }
        const container = queryFirst('.no-filter-results');
        if (container) container.parentElement.replaceChild(noFilterResults, container);
    }
}


/**
 * Update Section based off the selector passed
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @param {String} selector - Element selector
 * @return {undefined}
 */
function updateDOMElement(fragment, selector) {
    const elementToUpdate = queryFirst(selector, fragment);
    if (elementToUpdate) {
        const container = queryFirst(selector);
        if (container) container.parentElement.replaceChild(elementToUpdate, container);
    }
}
/**
 * Update Page Title, Description and Canonical URL with color filter
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {undefined}
 */
const updatePageTitle = fragment => {
    const pageTitle = queryFirst('.seo-page-title', fragment);
    const pageDescription = queryFirst('.seo-page-description');

    if (pageTitle) document.title = pageTitle.value;

    if (pageDescription) {
        const metaTag = queryFirst('meta[name="description"]');

        if (metaTag) metaTag.setAttribute('content', pageDescription.value);
    }
};

/**
 * Update plp filters swatch images
 *
 * @param {DocumentFragment} fragment - A DocumentFragment object
 * @return {undefined}
 */
function updatePatternImageSwatch(fragment) {
    const refinementPatterns = queryFirst('#refinement-print-and-solid', fragment);

    if (!refinementPatterns) return;

    const imgItems = queryAll('.refinement-image', refinementPatterns);

    imgItems.forEach(imageEl => {
        const { src, srcset } = imageEl.dataset;

        imageEl.src = src;

        if (srcset) imageEl.srcset = srcset;

        removeClass(imageEl, INVISIBLE_CLASS);
        imageEl.onload = () => addClass(imageEl, 'image-loaded');
    });

    const container = queryFirst('#refinement-print-and-solid');

    if (container) container.parentElement.replaceChild(refinementPatterns, container);
}

/**
 * Updates PLP products with out of stock text where applicable.
 * @param {Object} staleProductIds - A plain object of variation group IDs / inStock status
 */
function updateProducts(staleProductIds) {
    if (!staleProductIds || Object.keys(staleProductIds).length === 0) return;

    const productGrid = queryFirst('.product-grid');
    const currentProductIds = queryAll('.product', productGrid).map(node => node.dataset.pid).filter(Boolean);

    if (currentProductIds.length === 0) return;

    const oosProducts = Object.keys(staleProductIds).filter(productId => !staleProductIds[productId]);
    const { outOfStockMessage = '' } = productGrid.dataset;
    const oosClass = 'out-of-stock';

    oosProducts.forEach(productId => {
        const node = queryFirst(`.product[data-pid="${productId}"]`, productGrid);
        if (!node || node.classList.contains(oosClass)) return;

        const numSwatches = queryAll('.color-swatches button', node).length;
        // If there are multiple swatches, we don't know whether those are OOS too.
        if (numSwatches > 1) return;

        node.classList.add(oosClass);

        // If there's just 1 swatch, mark it as Sold Out.
        const swatches = queryFirst('.color-swatches .swatches', node);
        if (swatches) swatches.innerHTML = formatMessage(emptySwatchTemplate, outOfStockMessage);
    });
}

/**
 * @description Fetches the PLP results based on the price filter
 * @param {Object} element dom element
 * @param {string} actionUrl action url
 * @param {Object} [options] request options
 * @param {boolean} [options.isHistoryNavigation] true when re-rendering a history entry instead of applying a new filter
 * @param {Function} [options.onRendered] history only: owns the URL pagination and the viewport after render, receives the request id
 */
function getResultsOnPriceFilter(element, actionUrl, options) {
    const isHistoryNavigation = !!(options && options.isHistoryNavigation);
    const onRendered = isHistoryNavigation && typeof options.onRendered === 'function' ? options.onRendered : null;
    const prevActionUrl = actionUrl;
    const updatedActionUrl = isHistoryNavigation ? actionUrl : omitPaginationParams(appendStoreFilterQueryParams(element, actionUrl));
    const akamaiActionUrl = isAkamaiEnabled ? appendToUrl(updatedActionUrl, {
        'lp-cache-hash': lpCacheHash,
        'lp-group-hash': lpGroupHash
    }) : updatedActionUrl;
    const requestId = ++plpResultsRequestId;

    $.ajax({
        url: akamaiActionUrl,
        data: {
            selectedUrl: akamaiActionUrl
        },
        success: function (response, status, xhr) {
            if (requestId !== plpResultsRequestId) return;

            const serverTiming = xhr.getResponseHeader('server-timing') || '';
            const isCached = !!(serverTiming && serverTiming.split('desc=')[1].split(',')[0] === 'HIT');

            if (hasClass(element, 'clear-filters-btn') && ($('.no-filter-results').attr('data-productcount') === '0' || !akamaiActionUrl.includes('storeID'))) {
                $('.store-filter-toggle').prop('checked', false);
            }
            updateRefinementsAjaxUrl(prevActionUrl);

            const fragment = renderFragment(response);

            teardownInfiniteScrollObserver();
            if (!isHistoryNavigation) {
                updateUrlQueryParam(fragment);
            } else if (!onRendered) {
                stripPaginationFromBrowserUrl();
            }
            markPlpRendered(fragment);
            parseResults(fragment);
            updateSortOptions(fragment);
            updatePageTitle(fragment);
            showNoResultsText(fragment);
            const updatesCategoryContext = isHistoryNavigation || hasClass(element, 'cat-ref') || hasClass(element, 'cat-filter');
            if (updatesCategoryContext) {
                updateDOMElement(fragment, '.category-breadcrumb');
                updateDOMElement(fragment, '.search-banner');
            }
            const fragmentNoResults = queryFirst('.no-filter-results', fragment);
            const responseHasProducts = !fragmentNoResults || parseInt(fragmentNoResults.dataset.productcount, 10) > 0;
            if (responseHasProducts || updatesCategoryContext) {
                updateDOMElement(fragment, '.cat-result-count');
            }
            updateDOMElement(fragment, '.filter-sticky-panel.d-md-none');
            // update SEO Section (it depends on selected filters)
            updateDOMElement(fragment, '.category-seo-copy');

            const productCount = updateCount(fragment);

            updateFilterCount(fragment);
            updatePatternImageSwatch(fragment);

            const container = queryFirst('.no-filter-results');
            if (container) {
                if ($('.no-filter-results').attr('data-productcount') !== '0') {
                    addClass(container, HIDDEN_CLASS);
                } else {
                    removeClass(container, HIDDEN_CLASS);
                }
            }
            const noResultsSlots = queryFirst('.no-results-content-slots');
            if (noResultsSlots) {
                addClass(noResultsSlots, HIDDEN_CLASS);
            }
            $body.trigger('search:updateProducts', element); // LP custom changes end

            // Check Akamai cache
            if (isCached) {
                const cacheKey = encodeURIComponent(xhr.getResponseHeader('x-cache-key'));
                const productIds = queryAll('.product-grid .product').map(node => `${node.dataset.pid}:${!queryFirst('.swatches-empty', node)}`).filter(Boolean).join(',');
                const syncUrl = akamaiActionUrl.replace('ShowAjax', 'SyncShowAjax');

                clearTimeout(cacheSyncTimer);

                cacheSyncTimer = setTimeout(() => {
                    getJSON(syncUrl, 'POST', { productCount, cacheKey, productIds }, data => {
                        if (data.stale) {
                            // Update products with fresh stock status if we can
                            updateProducts(data.staleProductIds);

                            if (data.cacheInvalidated) {
                                // Akamai takes about 5 seconds to clear an entry
                                // Once that's complete, we should rebuild the cache with a fresh request
                                setTimeout(() => {
                                    $.ajax({
                                        url: akamaiActionUrl,
                                        data: {
                                            selectedUrl: akamaiActionUrl
                                        }
                                    });
                                }, akamaiSettings.cacheRefreshDelay);
                            }
                        }
                    });
                }, akamaiSettings.cacheSyncDelay);
            }

            $.spinner().stop();
            plpHistoryRestorePending = false;
            if (onRendered) {
                onRendered(requestId);
            } else {
                const scrollTop = getResultsTop();
                scrollTo(scrollTop, 0, function () {
                    if ($('.infinite-scroll-enabled').length > 0) {
                        initialiseSearchIntersection();
                    }
                });
            }
            // Attaching the hover event in new DOM loaded
            imageAndQuickbuyHoverHandler('.product-grid-container');
        },
        error: function () {
            if (requestId !== plpResultsRequestId) return;

            plpHistoryRestorePending = false;
            $.spinner().stop();
        }
    });
}

/**
 * Updates the browser URL to reflect the currently visible page based on scroll position.
 * Uses replaceState to avoid flooding the history stack.
 */
function updateUrlForVisiblePage() {
    const productGrid = queryFirst('.product-grid');
    if (!productGrid) return;

    const markers = queryAll('.page-boundary-marker[data-page-start]', productGrid);
    if (!markers.length) return;

    const headerHeight = mainHeader ? mainHeader.offsetHeight : 0;
    let currentPageStart = 0;

    for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].getBoundingClientRect().top <= headerHeight) {
            currentPageStart = parseInt(markers[i].dataset.pageStart, 10) || 0;
            break;
        }
    }

    const gridFooter = queryFirst('.grid-footer');
    const defaultPageSize = getDefaultPageSize(productGrid, gridFooter);
    const pageNum = getPageNumFromStart(currentPageStart, defaultPageSize);
    const url = new URL(window.location.href);

    url.searchParams.delete('start');
    url.searchParams.delete('sz');
    if (pageNum > 1) {
        url.searchParams.set('page', '' + pageNum);
    } else {
        url.searchParams.delete('page');
    }

    const newUrl = url.pathname + url.search + url.hash;
    if (newUrl !== window.location.pathname + window.location.search + window.location.hash) {
        window.history.replaceState(window.history.state, '', newUrl);
        markPlpRendered();
        persistPlpEntryState();
    }
}

/**
 * Extracts the page start value from an AJAX response fragment.
 * @param {DocumentFragment} fragment - parsed AJAX response
 * @returns {number} the start index of the response page
 */
function getPageStartFromFragment(fragment) {
    const gridFooter = queryFirst('.grid-footer', fragment);
    return gridFooter ? parseInt(gridFooter.dataset.pageStart, 10) || 0 : 0;
}

/**
 * Inserts a page boundary marker into the product grid.
 * @param {number} pageStart - the start index for this page
 * @param {Element} [beforeElement] - insert before this element; if null, appends to grid
 */
function insertPageMarker(pageStart, beforeElement) {
    const marker = document.createElement('div');
    marker.className = 'page-boundary-marker';
    marker.dataset.pageStart = pageStart;
    marker.style.height = '0';
    marker.style.overflow = 'hidden';

    const productGrid = queryFirst('.product-grid');
    if (beforeElement && productGrid) {
        productGrid.insertBefore(marker, beforeElement);
    } else if (productGrid) {
        const gridFooter = queryFirst('.grid-footer', productGrid);
        if (gridFooter) {
            productGrid.insertBefore(marker, gridFooter);
        } else {
            productGrid.appendChild(marker);
        }
    }
}

/**
 * Reveals tiles hidden as featured page-size overflow (show-more / backfill continuity).
 * @param {ParentNode} [root] - scope to search within (defaults to document)
 */
function unhideFeaturedOverflow(root) {
    queryAll('.product-tile-container.featured-overflow', root || document).forEach(function (el) {
        removeClass(el, 'featured-overflow');
        removeClass(el, 'd-none');
        el.removeAttribute('aria-hidden');
    });
}

/**
 * @param {string} showMoreUrl URL used to fetch new products
 * @param {Object} e Event causing this update
 * @param {boolean} isPagination Boolean true if this is a pagination-based update; false if "Show More"
 * @param {Object} observerObj IntersectionObserver context for cleanup
 * @param {boolean} isBackButton true if loading because of a back button click
 * @param {Function} [onLoaded] called after the new tiles are in the grid
 */
function loadProducts(showMoreUrl, e, isPagination, observerObj, isBackButton, onLoaded) {
    observerObj = observerObj ?? {};
    isBackButton = isBackButton ?? false;

    $.spinner().start();
    $body.trigger('search:showMore', e);

    if (isPagination) {
        scrollTo(getResultsTop());
    }

    const akamaiActionUrl = isAkamaiEnabled ? appendToUrl(showMoreUrl, {
        'lp-cache-hash': lpCacheHash,
        'lp-group-hash': lpGroupHash
    }) : showMoreUrl;

    $.ajax({
        url: akamaiActionUrl,
        data: { selectedUrl: akamaiActionUrl },
        success: (response, status, xhr) => {
            const serverTiming = xhr.getResponseHeader('server-timing') || '';
            const isCached = !!(serverTiming && serverTiming.split('desc=')[1].split(',')[0] === 'HIT');

            const fragment = renderFragment(response);
            let newPageStart = getPageStartFromFragment(fragment);
            if (!newPageStart) {
                try {
                    const reqUrl = new URL(showMoreUrl, window.location.origin);
                    newPageStart = parseInt(reqUrl.searchParams.get('start'), 10) || 0;
                    if (!newPageStart) {
                        const pageFromUrl = parseInt(reqUrl.searchParams.get('page'), 10) || 0;
                        if (pageFromUrl > 1) {
                            const defaultPageSize = getDefaultPageSize(
                                queryFirst('.product-grid'),
                                queryFirst('.grid-footer')
                            );
                            newPageStart = (pageFromUrl - 1) * defaultPageSize;
                        }
                    }
                } catch (ignored) { newPageStart = 0; }
            }

            if (!isPagination) {
                unhideFeaturedOverflow();
            }

            if (isPagination) {
                $('.product-grid').html(response);
            } else {
                insertPageMarker(newPageStart);
                $('.grid-footer').replaceWith(response);
            }

            updateSortOptions(fragment);
            if (!isBackButton) {
                const productGrid = queryFirst('.product-grid');
                const urlScrollSync = !!productGrid && productGrid.dataset.urlScrollSync === 'true';
                // With URL scroll sync the visible page owns the URL, so show more must not add an entry the sync then rewrites.
                if (isPagination || !urlScrollSync) {
                    updateUrlQueryParam(fragment);
                }
                markPlpRendered();
                if (window.history.scrollRestoration) {
                    window.history.scrollRestoration = 'manual';
                }
            }

            const productCount = updateCount(fragment);

            $body.trigger('search:updateProducts');

            if (isCached) {
                const cacheKey = encodeURIComponent(xhr.getResponseHeader('x-cache-key'));
                const gridFooter = queryFirst('.grid-footer', fragment);
                const pageSize = (gridFooter && parseInt(gridFooter.dataset.pageSize)) || 48;
                const productIds = queryAll('.product-grid .product').slice(-pageSize).map(node => `${node.dataset.pid}:${!queryFirst('.swatches-empty', node)}`).filter(Boolean).join(',');
                const syncUrl = akamaiActionUrl.replace('UpdateGrid', 'SyncUpdateGrid');

                clearTimeout(cacheSyncTimer);

                cacheSyncTimer = setTimeout(() => {
                    getJSON(syncUrl, 'POST', { productCount, cacheKey, productIds }, data => {
                        if (data.stale) {
                            updateProducts(data.staleProductIds);

                            if (data.cacheInvalidated) {
                                setTimeout(() => {
                                    $.ajax({
                                        url: akamaiActionUrl,
                                        data: {
                                            selectedUrl: akamaiActionUrl
                                        }
                                    });
                                }, akamaiSettings.cacheRefreshDelay);
                            }
                        }
                    });
                }, akamaiSettings.cacheSyncDelay);
            }

            $.spinner().stop();

            imageAndQuickbuyHoverHandler('.product-grid-container');
            if (observerObj && observerObj.target) {
                observerObj.observer.unobserve(observerObj.target);
            }

            // The grid replacement drops the observed show-more target, so the observer
            // has to be rebuilt even when this load did not come from it.
            if ($('.infinite-scroll-enabled').length > 0) {
                initialiseSearchIntersection();
            }

            if (typeof onLoaded === 'function') {
                onLoaded();
            }
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

/**
 * Backfills previous pages when landing on a PLP with start > 0.
 * Loads prior pages sequentially (product cells only, content cells are dropped), then replaces
 * skeleton tiles in place with those cells in a single DOM operation.
 * @param {Function} [onComplete] - callback after backfill finishes or is skipped
 */
function backfillPreviousPages(onComplete) {
    function done() {
        if (typeof onComplete === 'function') {
            onComplete();
        }
    }

    const productGrid = queryFirst('.product-grid');
    if (!productGrid || queryFirst('.out-of-range-page')) {
        done();
        return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const gridFooter = queryFirst('.grid-footer');
    const MAX_START = 960;
    const MIN_PAGE_SIZE = 12;
    const currentStart = Math.min(
        getCurrentStartFromUrl(urlParams, productGrid, gridFooter),
        MAX_START
    );
    const pageSize = Math.max(
        getDefaultPageSize(productGrid, gridFooter),
        MIN_PAGE_SIZE
    );

    if (!currentStart) {
        done();
        return;
    }

    const skeletons = queryAll('.skeleton-tile', productGrid);
    if (!skeletons.length) {
        done();
        return;
    }

    const updateGridUrlEl = queryFirst('.ajax-update-grid-url');
    let baseUrl = (updateGridUrlEl && updateGridUrlEl.value) || null;

    if (!baseUrl) {
        const showMoreBtn = gridFooter && queryFirst('.show-more button', gridFooter);
        baseUrl = showMoreBtn ? showMoreBtn.dataset.url : null;
    }
    if (!baseUrl) {
        done();
        return;
    }

    const noFilterResults = queryFirst('.no-filter-results');
    const totalProducts = noFilterResults ? parseInt(noFilterResults.dataset.productcount, 10) : NaN;
    const effectiveStart = !isNaN(totalProducts) && currentStart >= totalProducts
        ? Math.max(totalProducts - pageSize, 0)
        : currentStart;

    const pagesToLoad = [];
    for (let s = 0; s < effectiveStart; s += pageSize) {
        pagesToLoad.push(s);
    }
    if (!pagesToLoad.length) {
        done();
        return;
    }

    const currentPageMarker = queryFirst('.page-boundary-marker', productGrid);
    if (!currentPageMarker) {
        done();
        return;
    }

    const suppressOverflowAnchor = getNavigationType() === 'navigate';
    if (suppressOverflowAnchor) {
        document.documentElement.style.overflowAnchor = 'none';
    }

    const baseUrlObj = new URL(baseUrl, window.location.origin);
    const featuredFromPage = urlParams.get('featured');
    if (featuredFromPage && !baseUrlObj.searchParams.get('featured')) {
        baseUrlObj.searchParams.set('featured', featuredFromPage);
    }
    const collectedPages = [];
    let backfillAborted = false;

    /**
     * Replaces skeleton tiles with real tiles in place in a single DOM batch: the i-th collected product cell
     * takes the place of the i-th skeleton, so content cells rendered in the skeleton zone keep their position.
     * Each page boundary marker goes right before the first cell of its page. Cells left after the last skeleton
     * go before the current page marker; skeletons left after the last cell are removed.
     */
    function insertAllPages() {
        const skeletonTiles = queryAll('.skeleton-tile', productGrid);
        let skeletonIndex = 0;

        collectedPages.sort(function (a, b) { return a.pageStart - b.pageStart; });

        collectedPages.forEach(function (page) {
            const marker = document.createElement('div');
            marker.className = 'page-boundary-marker';
            marker.dataset.pageStart = page.pageStart.toString();
            marker.style.cssText = 'height:0;overflow:hidden';
            productGrid.insertBefore(marker, skeletonTiles[skeletonIndex] || currentPageMarker);

            if (page.tiles) {
                page.tiles.forEach(function (tile) {
                    const skeleton = skeletonTiles[skeletonIndex++];
                    if (skeleton) {
                        skeleton.replaceWith(tile);
                    } else {
                        productGrid.insertBefore(tile, currentPageMarker);
                    }
                });
            }
        });

        skeletonTiles.slice(skeletonIndex).forEach(function (s) { s.remove(); });

        unhideFeaturedOverflow(productGrid);

        if (suppressOverflowAnchor) {
            document.documentElement.style.overflowAnchor = '';
        }

        imageAndQuickbuyHoverHandler('.product-grid-container');
        $body.trigger('search:updateProducts');
    }

    function finishBackfill() {
        insertAllPages();
        $('.filter-sticky-panel').spinner().stop();
        done();
    }

    /**
     * Loads backfill pages sequentially and stops when a chunk returns no products.
     * @param {number} pageIndex - index into pagesToLoad
     */
    function loadPageAtIndex(pageIndex) {
        if (backfillAborted || pageIndex >= pagesToLoad.length) {
            finishBackfill();
            return;
        }

        const pageStartVal = pagesToLoad[pageIndex];
        const fetchUrl = new URL(baseUrlObj.href);
        fetchUrl.searchParams.delete('start');
        fetchUrl.searchParams.delete('sz');
        const pageNum = Math.floor(pageStartVal / pageSize) + 1;
        if (pageNum > 1) {
            fetchUrl.searchParams.set('page', String(pageNum));
        } else {
            fetchUrl.searchParams.delete('page');
        }

        let finalUrl = fetchUrl.pathname + fetchUrl.search;
        if (isAkamaiEnabled) {
            finalUrl = appendToUrl(finalUrl, {
                'lp-cache-hash': lpCacheHash,
                'lp-group-hash': lpGroupHash
            });
        }

        $.ajax({
            url: finalUrl,
            data: { selectedUrl: finalUrl },
            success: function (response) {
                if (backfillAborted) return;

                const tempContainer = document.createElement('div');
                tempContainer.innerHTML = response;
                const tiles = queryAll('.product-tile-container', tempContainer);

                if (!tiles.length) {
                    backfillAborted = true;
                    finishBackfill();
                    return;
                }

                collectedPages.push({
                    pageStart: pageStartVal,
                    tiles: tiles
                });
                loadPageAtIndex(pageIndex + 1);
            },
            error: function () {
                backfillAborted = true;
                finishBackfill();
            }
        });
    }

    $('.filter-sticky-panel').spinner().start();
    loadPageAtIndex(0);
}

/**
 * Function to apply store filter on PLP page
 * @param {HTMLElement} checkboxToggleEl Store filter toggle checkbox element
 * @param {boolean} appendStoreID Flag to append storeID to URL params
 */
function applyStoreFilter(checkboxToggleEl) {
    const refinementsContainer = queryFirst(REFINEMENTS_CONTAINER_SELECTOR);
    if (!refinementsContainer) return;

    const { actionUrl } = refinementsContainer.dataset;

    // Perform Filter
    $.spinner().start();
    getResultsOnPriceFilter(checkboxToggleEl, actionUrl);
}

/**
 * Update The filter count if storefilter is applied on load
 */
$(function () {
    const selectedFilterCountEl = queryFirst('.selected-filter-count');
    const noResultsRecommendationPage = queryFirst('.no-results-recommendations');
    const noFilterResults = queryFirst('.no-filter-results');

    if (noResultsRecommendationPage) {

        removeClass(noResultsRecommendationPage, HIDDEN_CLASS);
    }
    if (selectedFilterCountEl) {
        const refinementFilterText = refinementFilterCount(selectedFilterCountEl);
        selectedFilterCountEl.textContent = refinementFilterText;
    }

    // On page reload or category change, if products are available
    // hide the clear filter (no results section)

    if (noFilterResults) {
        const numOfProducts = noFilterResults.dataset.productcount;
        if (numOfProducts > 0) {
            addClass(noFilterResults, HIDDEN_CLASS);
        } else {
            removeClass(noFilterResults, HIDDEN_CLASS);
        }
    }
});


$(document).ready(function () {
    const urlParams = new URLSearchParams(window.location.search);
    const productGrid = queryFirst('.product-grid');
    const gridFooter = queryFirst('.grid-footer');

    imageAndQuickbuyHoverHandler('.product-grid-container');

    if (productGrid) {
        syncScrollRestorationMode();
        markPlpRendered();

        document.addEventListener('click', persistClickedTile, true);
        // The throttle drops the last events of a scroll, so a debounced call writes where the scroll ended.
        window.addEventListener('scroll', throttle(persistPlpEntryState, 1000, debounce(persistPlpEntryState, 150)));
        // A replaceState in pagehide is not a reliable last write before a reload, so the panel state is also written when it changes.
        $body.on('toggle.lp.filters', function () {
            persistFilterPanelState();
            // The grid width settles only after the panel slide animation, so the anchor tile is measured afterwards.
            setTimeout(persistPlpEntryState, FILTER_PANEL_ANIMATION_MS);
        });
        window.addEventListener('pagehide', function () {
            persistPlpEntryState();
            claimScrollRestoration();
        });
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'hidden') {
                persistPlpEntryState();
                claimScrollRestoration();
            }
        });

        const initialStart = getCurrentStartFromUrl(urlParams, productGrid, gridFooter);

        const initialMarker = document.createElement('div');
        initialMarker.className = 'page-boundary-marker';
        initialMarker.dataset.pageStart = initialStart;
        initialMarker.style.height = '0';
        initialMarker.style.overflow = 'hidden';

        const firstRealTile = queryFirst('.product-tile-container:not(.skeleton-tile)', productGrid);
        if (firstRealTile) {
            productGrid.insertBefore(initialMarker, firstRealTile);
        } else {
            productGrid.insertBefore(initialMarker, productGrid.firstChild);
        }

        if (productGrid.dataset.urlScrollSync === 'true') {
            window.addEventListener('scroll', throttle(updateUrlForVisiblePage, 250, debounce(updateUrlForVisiblePage, 150)));
        }
        startPlpScrollRestore(initialStart);
        backfillPreviousPages(function () {
            initInfiniteScrollIfEnabled();
            onPlpBackfillDone(initialStart);
        });
    } else {
        initInfiniteScrollIfEnabled();
    }
});

window.onload = () => {
    if (queryFirst('.product-grid')) {
        startPlpScrollRestore(getDocumentPageStart());
    }
};

window.addEventListener('pageshow', function (event) {
    if (!queryFirst('.product-grid') || event.persisted) {
        return;
    }
    syncScrollRestorationMode();
    startPlpScrollRestore(getDocumentPageStart());
});

/**
 * Method to initialise the  intersection observer
 *
 */
function initialiseSearchIntersection() {
    const backToTopButtonEL = queryFirst('.back-to-top-icon');
    const observerTarget = document.querySelector('.show-more.lazy-load-target');
    const options = {
        threshold: 1
    };

    if (searchIntersectionObserver) {
        searchIntersectionObserver.disconnect();
        searchIntersectionObserver = null;
    }

    if (!observerTarget) {
        return;
    }

    /**
     * Represents a callback function which will be called when the observed target element enters the viewport
     * @param {Array} entries IntersectionObserverEntry Objects
     */
    function handleIntersect(entries) {
        entries.forEach(function (entry) {
            if (entry.isIntersecting) {
                const targetURL = $('.show-more button').data('url');
                if (!targetURL) return;
                const pagination = '';
                const infiniteScrollEvent = new Event('infiniteScrollEvent');
                const observerObj = {
                    observer: observer,
                    target: observerTarget
                };
                loadProducts(targetURL, infiniteScrollEvent, !!pagination, observerObj);
                observer.unobserve(observerTarget);

                if (hasClass(backToTopButtonEL, HIDDEN_CLASS)) {
                    removeClass(backToTopButtonEL, HIDDEN_CLASS);
                }
            } else {
                return;
            }
        });
    }
    const observer = new IntersectionObserver(handleIntersect, options);
    searchIntersectionObserver = observer;
    observer.observe(observerTarget);
}

/**
 * Paging bar mode: replaces the grid with the page of the history entry the browser moved to.
 * @param {PopStateEvent} e - popstate event
 */
function reloadPagedGridFromHistory(e) {
    const baseUrlElem = queryFirst('.ajax-base-url') || queryFirst('.ajax-update-grid-url');
    const baseUrlWithParams = baseUrlElem && baseUrlElem.value;
    if (!baseUrlWithParams) {
        return;
    }
    const baseUrl = new URL(baseUrlWithParams, window.location.origin);
    const baseParams = new URLSearchParams(baseUrl.search);
    const qsParams = e.state && e.state.path;

    if (qsParams) {
        const newParams = new URLSearchParams(qsParams.split('?')[1] || '');
        newParams.forEach((value, key) => {
            baseParams.set(key, value);
        });
    }

    const finalUrl = baseUrl.origin + baseUrl.pathname + '?' + baseParams.toString();
    loadProducts(finalUrl, e, true, null, true);
}

/**
 * @param {number} pageStart - zero-based product offset of the history entry
 */
function placeViewportForHistoryEntry(pageStart) {
    const target = getHistoryEntryTarget(pageStart);
    if (target) {
        scrollWindowToElement(target.element, target.viewportTop);
    }
}

/**
 * Infinite scroll mode: shows the page of the history entry the browser moved to without replacing the grid.
 * @param {PopStateEvent} e - popstate event
 */
function restorePageFromHistory(e) {
    const productGrid = queryFirst('.product-grid');
    const gridFooter = queryFirst('.grid-footer');
    const pageSize = getDefaultPageSize(productGrid, gridFooter);
    const targetStart = getStartFromUrlParams(new URLSearchParams(window.location.search), pageSize);

    if (!targetStart || queryFirst('.page-boundary-marker[data-page-start="' + targetStart + '"]', productGrid)) {
        placeViewportForHistoryEntry(targetStart);
        return;
    }

    const showMoreButton = queryFirst('.show-more button', gridFooter);
    const showMoreUrl = showMoreButton && showMoreButton.dataset.url;
    const nextStart = showMoreUrl
        ? getStartFromUrlParams(new URL(showMoreUrl, window.location.origin).searchParams, pageSize)
        : -1;

    if (nextStart === targetStart) {
        loadProducts(showMoreUrl, e, false, null, true, function () {
            placeViewportForHistoryEntry(targetStart);
        });
        return;
    }

    window.location.reload();
}

/**
 * Re-renders the PLP results for the history entry the browser just moved to, without adding an entry.
 * A deep entry (?page=N) comes back at page N with skeletons above it, then the previous pages are backfilled.
 * A Page Designer grid reloads for an unrefined target, because ShowAjax cannot render its content regions.
 */
function refreshResultsFromHistory() {
    if (isPdGridDocument && !isHistoryEntryRefined()) {
        window.location.reload();
        return;
    }

    const baseUrlInput = queryFirst('.ajax-show-base-url');
    if (!baseUrlInput || !baseUrlInput.value) {
        window.location.reload();
        return;
    }

    const showAjaxUrl = buildShowAjaxUrlForLocation(baseUrlInput.value);
    const pageSize = getDefaultPageSize(queryFirst('.product-grid'), queryFirst('.grid-footer'));
    const targetStart = getStartFromUrlParams(new URLSearchParams(window.location.search), pageSize);

    syncStoreToggleWithLocation();
    $.spinner().start();
    // The traversal restores the entry's scrollY over the outgoing grid; its anchor must not overwrite the entry's tile.
    plpHistoryRestorePending = true;

    if (!targetStart) {
        getResultsOnPriceFilter(null, showAjaxUrl, {
            isHistoryNavigation: true,
            onRendered: function () {
                initInfiniteScrollIfEnabled();
                const target = getPersistedTileTarget();
                if (target) {
                    scrollWindowToElement(target.element, target.viewportTop);
                } else {
                    scrollTo(getResultsTop(), 0);
                }
            }
        });
        return;
    }

    getResultsOnPriceFilter(null, appendToUrl(showAjaxUrl, { page: getPageNumFromStart(targetStart, pageSize) }), {
        isHistoryNavigation: true,
        onRendered: function (requestId) {
            const productGrid = queryFirst('.product-grid');
            const firstRealTile = productGrid && queryFirst('.product-tile-container:not(.skeleton-tile)', productGrid);
            insertPageMarker(targetStart, firstRealTile || (productGrid && productGrid.firstChild));
            if (typeof window.sizePlpSkeletonTiles === 'function') {
                window.sizePlpSkeletonTiles();
            }

            plpHistoryRestorePending = true;
            placeViewportForHistoryEntry(targetStart);
            backfillPreviousPages(function () {
                if (requestId !== plpResultsRequestId) return;
                plpHistoryRestorePending = false;
                initInfiniteScrollIfEnabled();
                placeViewportForHistoryEntry(targetStart);
            });
        }
    });
}

baseSearch.showMore = function () {
    const productGrid = queryFirst('.product-grid');
    const pagination = queryFirst('.pagination', productGrid);
    // Show more, traditional, and new pagination link event to load new products
    $container.on('click', '.show-more button, .product-grid .pagination a', function (e) {
        e.stopPropagation();
        e.preventDefault();

        const { url } = this.dataset;

        if (!url) return;

        loadProducts(url, e, !!pagination);
    });

    const { getNestedValue } = require('../util');

    if (pagination && hasClass(pagination, 'new-pagination')) {
        $container.on('change', '.pagination-pages', e => {
            e.stopPropagation();
            loadProducts(getNestedValue(e, 'target.selectedOptions[0].dataset.url'), e, true);
        });
    }

    window.addEventListener('popstate', e => {
        const targetUrl = window.location.href;

        if (getUrlPathname(targetUrl) !== getUrlPathname(renderedPlpUrl)) {
            window.location.reload();
            return;
        }

        if (getPlpConfigKey(targetUrl) !== getPlpConfigKey(renderedPlpUrl)) {
            refreshResultsFromHistory();
            return;
        }

        // Safari fires popstate for the entry that brings Back into this document; its URL is the one already shown.
        if (getUrlKey(targetUrl) === getUrlKey(renderedPlpUrl)) {
            return;
        }

        markPlpRendered();

        if (pagination) {
            reloadPagedGridFromHistory(e);
            return;
        }

        restorePageFromHistory(e);
    });
};

baseSearch.applyFilter = function () {
    function onRefinement(e) {
        const target = e.currentTarget || this;
        let isSelected = hasClass(target, 'selected');
        let isCatfilter = hasClass(target, 'cat-ref');
        if (isSelected && isCatfilter) {
            return;
        }

        if (hasClass(target, 'store-filter-pill')) {
            $('.store-filter-toggle').prop('checked', false);
        }
        const { dataset } = target;

        e.preventDefault();
        e.stopPropagation();

        $.spinner().start();
        $(target).trigger('search:filter', e);
        const actionUrl = dataset.href || dataset.actionUrl;
        getResultsOnPriceFilter(target, actionUrl);
    }

    // Handle refinement value selection and reset click
    $container.on('click', '.refinements li button, .refinement-bar button.reset, .filter-value button, .swatch-filter button', onRefinement);

    $container.on('submit', '.price-filter-form', function (e) {
        e.preventDefault();
        e.stopPropagation();

        $.spinner().start();
        $(this).trigger('search:filter', e);
        const { target } = e;
        const applyButton = queryFirst('.price-apply-button', target);
        const actionUrl = $(applyButton).attr('data-href');
        getResultsOnPriceFilter(applyButton, actionUrl);
    });

    // Prevent checkbox/toggle change is store id attribute is not present
    $container.on('click', '.store-filter-toggle', (event) => {
        if (!event.target.dataset.storeId) {
            event.preventDefault();
        }
    });

    // Toggle checkbox change event
    $container.on('change', '.store-filter-toggle', (event) => {
        applyStoreFilter(event.target);
    });

    // Selecting a store from the drawer
    $searchResultsContainer.on('filter:updateStoreId', (_e, data) => {
        // Updating new storeId on DOM
        const storeFilterToggleEl = queryFirst('.store-filter-toggle');
        const { storeId, storeName, storeCity, storeStateCode } = data;

        if (storeId) {
            // Updating toggle data attrs and state
            storeFilterToggleEl.dataset.storeId = storeId;
            storeFilterToggleEl.dataset.storeName = storeName;
            storeFilterToggleEl.disabled = false;
            storeFilterToggleEl.checked = true;

            // Updating Change store link
            const buttonLabel = `${storeName || ''} - ${storeCity || ''}, ${storeStateCode}`;
            const storeSelectorEl = queryFirst('.btn-in-store-pickup');
            if (storeSelectorEl) {
                storeSelectorEl.textContent = buttonLabel;
            }

            // Apply a store filter
            applyStoreFilter(storeFilterToggleEl);
        }
    });
    // Allow store filter to be seen / clicked now that the event handler is in place
    /** @type {HTMLInputElement} */
    const storeFilterToggle = queryFirst('.store-filter .switch');
    /** @type {HTMLInputElement} */
    const storeToggleCTA = queryFirst('.store-filter-toggle');
    if (storeFilterToggle) {
        storeToggleCTA.disabled = false;
        removeClass(storeFilterToggle, INVISIBLE_CLASS);
    }
};

baseSearch.updatePrice = function () {
    $container.on('click', '.refinement-price .values button', () => {
        addClass(queryFirst('.values button', queryFirst('.refinement-price')), 'is-not-custom');
    });
    $container.on('focus', '.refinement-price input', () => {
        removeClass(queryFirst('.price-filter', queryFirst('.refinement-price')), 'is-not-custom');
    });
    // prevent negative value
    $container.on('keydown', '.refinement-price .price-filter-min, .refinement-price .price-filter-max', e => {
        if (!((e.keyCode > 95 && e.keyCode < 106) || (e.keyCode > 47 && e.keyCode < 58) || e.keyCode == 8 || e.keyCode == 9 || e.keyCode == 38 || e.keyCode == 40 || e.keyCode == 13)) {
            return false;
        }
    });

    $container.on('keyup', '.refinement-price .price-filter-min, .refinement-price .price-filter-max', e => {
        const refinementPrice = queryFirst('.refinement-price');
        const minPrice = queryFirst('.price-filter-min', refinementPrice).value;
        const maxPrice = queryFirst('.price-filter-max', refinementPrice).value;
        const applyButton = queryFirst('.price-apply-button', refinementPrice);
        removeClass(queryAll('.values button', refinementPrice), 'selected');
        if (minPrice) {
            queryFirst('.price-filter-max', refinementPrice).min = parseInt(minPrice) + 1; // setting min value for max input
        }

        // Enable or disable apply button based min and max filter value
        if (minPrice && maxPrice && parseInt(minPrice) < parseInt(maxPrice)) {
            applyButton.disabled = false;
        } else {
            applyButton.disabled = true;
        }
    });

    $container.on('change', '.refinement-price .price-filter-min, .refinement-price .price-filter-max', e => {
        const refinementPrice = queryFirst('.refinement-price');
        const minPrice = queryFirst('.price-filter-min', refinementPrice).value;
        const maxPrice = queryFirst('.price-filter-max', refinementPrice).value;
        const applyButton = queryFirst('.price-apply-button', refinementPrice);
        const uri = applyButton.dataset.href;

        let newUrl = '';

        if (minPrice && maxPrice) {
            const updateQueryStringParameter = (key, value) => {
                const re = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
                const separator = uri.indexOf('?') !== -1 ? '&' : '?';
                if (uri.match(re)) {
                    newUrl = newUrl ? newUrl.replace(re, '$1' + key + '=' + value + '$2') : uri.replace(re, '$1' + key + '=' + value + '$2');
                } else {
                    newUrl = newUrl ? newUrl + separator + key + '=' + value : uri + separator + key + '=' + value;
                }
            };

            updateQueryStringParameter('pmin', parseInt(minPrice, 10));
            updateQueryStringParameter('pmax', parseInt(maxPrice, 10));
            applyButton.dataset.href = newUrl;
        }
    });
};

/**
 * Show the filter refinements UI
 */
function showRefinements() {
    $refinementBar.show();
    $modalBG.show();
    $refinementBar.siblings().attr('aria-hidden', true);
    $refinementBar.closest('.row').siblings().attr('aria-hidden', true);
    $refinementBar.closest('.tab-pane.active').siblings().attr('aria-hidden', true);
    $refinementBar.closest('.container.search-results').siblings().attr('aria-hidden', true);
    $refinementBar.find('.close').trigger('focus');
}

/**
 * Hide the filter refinements UI
 */
function hideRefinements() {
    $refinementBar.hide();
    $modalBG.hide();
    $refinementBar.siblings().attr('aria-hidden', false);
    $refinementBar.closest('.row').siblings().attr('aria-hidden', false);
    $refinementBar.closest('.tab-pane.active').siblings().attr('aria-hidden', false);
    $refinementBar.closest('.container.search-results').siblings().attr('aria-hidden', false);
}

baseSearch.filter = function () {
    // Display refinements bar when Menu icon clicked
    $container.on('click', 'button.filter-results', function () {
        showRefinements();

        // LP custom changes start
        $body.addClass('modal-open');
        // LP custom changes end
    });
};

baseSearch.closeRefinements = function () {
    // Refinements close button
    $container.on('click', '.refinement-bar button.close, .modal-background', function () {
        hideRefinements();

        const filterResults = queryFirst('.filter-results');

        if (filterResults) filterResults.focus();

        // LP custom changes start
        $body.removeClass('modal-open');
        // LP custom changes end
    });
};

// Shadows the base handler, which hides the refinement bar on every resize - on iOS
// scrolling fires resize and would close the open filter panel.
baseSearch.resize = function () {};

baseSearch.sortHandler = function () {
    $body.on('search:sortOptions', function (e, data) {
        const { response } = data;
        const fragment = renderFragment(response);

        teardownInfiniteScrollObserver();
        updateUrlAfterPaginationReset(fragment);
        markPlpRendered(fragment);
        parseResults(fragment);
        const sortText = updateSortOptions(fragment);
        showNoResultsText(fragment);
        updateCount(fragment);
        updateFilterCount(fragment);
        updatePatternImageSwatch(fragment);
        // Attaching the hover event in new DOM loaded
        imageAndQuickbuyHoverHandler('.product-grid-container');
        const scrollTop = getResultsTop();
        scrollTo(scrollTop, 0, function () {
            if ($('.infinite-scroll-enabled').length > 0) {
                initialiseSearchIntersection();
            }
        });
        $body.trigger('search:updateProducts', { sortText: sortText.toLowerCase() });
    });
};

module.exports = baseSearch;
